import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Copy, Loader2, Ticket, MessageSquareQuote, ShieldAlert,
  Mail, MessageSquare, Link as LinkIcon, UserPlus, CheckCircle2, Clock, Send,
  HardDrive, Activity, AlertTriangle, Gauge,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import AdminBetaFeedback from "@/components/AdminBetaFeedback";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

const APP_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const inviteUrl = (code) => `${APP_ORIGIN}/auth?invite=${code}`;
const smsText = (code, days, plan) =>
  `You're invited to Discrimina (${days}d ${plan}). Sign up with code ${code}: ${inviteUrl(code)}`;

export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [codes, setCodes] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogFilter, setEmailLogFilter] = useState("all"); // all | beta_invite | magic_invite | digest_weekly | trial_ending | feedback | digest_feedback
  const [migrateStatus, setMigrateStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthRefreshing, setHealthRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ count: 5, days: 60, plan: "clinic", note: "", recipients: "", access_type: "auto" });
  const [attaching, setAttaching] = useState({}); // { [codeId]: email }
  const [pending, setPending] = useState({}); // { [codeId]: 'email' | 'attach' }

  useEffect(() => {
    if (user && !user.is_platform_admin) nav("/learners");
  }, [user, nav]);

  const load = async () => {
    try {
      const [c, f, e, m, h] = await Promise.all([
        http.get("/admin/beta-codes"),
        http.get("/admin/feedback"),
        http.get("/admin/email-log"),
        http.get("/admin/migrate-images/status").catch(() => ({ data: null })),
        http.get("/admin/system-health").catch(() => ({ data: null })),
      ]);
      setCodes(c.data.codes || []);
      setFeedback(f.data.items || []);
      setEmailLog(e.data.items || []);
      setMigrateStatus(m.data);
      setHealth(h.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };
  useEffect(() => { if (user?.is_platform_admin) load(); /* eslint-disable-next-line */ }, [user]);

  const refreshHealth = async () => {
    setHealthRefreshing(true);
    try {
      const r = await http.get("/admin/system-health");
      setHealth(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setHealthRefreshing(false);
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    try {
      // Batch of 100 with server-side concurrency=10 finishes in ~15 s — well under CF's 100 s edge timeout.
      const r = await http.post("/admin/migrate-images", { limit: 100 });
      toast.success(`Migrated ${r.data.migrated} · Failed ${r.data.failed} · Pending ${r.data.pending}`);
      const s = await http.get("/admin/migrate-images/status");
      setMigrateStatus(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  const [shrinking, setShrinking] = useState(false);
  const runShrink = async () => {
    if (!window.confirm(`This clears the base64 backup on ${migrateStatus?.shrinkable || 0} card(s) that already live in object storage. Continue?`)) return;
    setShrinking(true);
    try {
      const r = await http.post("/admin/shrink-images-db", { limit: 2000 });
      toast.success(`Cleared ${r.data.cleared} · Remaining ${r.data.shrinkable}`);
      const s = await http.get("/admin/migrate-images/status");
      setMigrateStatus(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Shrink failed");
    } finally {
      setShrinking(false);
    }
  };

  const parsedRecipients = useMemo(() => {
    return (form.recipients || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
  }, [form.recipients]);

  const effectiveCount = parsedRecipients.length > 0 ? parsedRecipients.length : form.count;

  const create = async () => {
    setBusy(true);
    try {
      const body = {
        count: parsedRecipients.length > 0 ? parsedRecipients.length : form.count,
        days: form.days,
        plan: form.plan,
        note: form.note,
        access_type: form.access_type,
      };
      if (parsedRecipients.length > 0) {
        body.recipients = parsedRecipients;
        body.send_email = true;
      }
      const r = await http.post("/admin/beta-codes", body);
      const sent = (r.data.emails || []).filter((e) => e.sent).length;
      if (parsedRecipients.length > 0) {
        toast.success(`Generated ${r.data.codes.length} · Emailed ${sent}/${parsedRecipients.length}`);
      } else {
        toast.success(`Generated ${r.data.codes.length} code${r.data.codes.length === 1 ? "" : "s"}`);
      }
      setForm((s) => ({ ...s, recipients: "", note: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const copyLink = (code) => {
    navigator.clipboard?.writeText(inviteUrl(code));
    toast.success("Signup link copied");
  };

  const sendEmailFor = async (c) => {
    if (!c.recipient_email) return;
    setPending((p) => ({ ...p, [c.id]: "email" }));
    try {
      await http.post(`/admin/beta-codes/${c.id}/send-email`);
      toast.success(`Emailed ${c.recipient_email}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally {
      setPending((p) => { const n = { ...p }; delete n[c.id]; return n; });
    }
  };

  const attachRecipient = async (c) => {
    const email = (attaching[c.id] || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    setPending((p) => ({ ...p, [c.id]: "attach" }));
    try {
      await http.patch(`/admin/beta-codes/${c.id}`, { recipient_email: email });
      // Fire off the invite email immediately after attaching
      await http.post(`/admin/beta-codes/${c.id}/send-email`).catch(() => {});
      toast.success(`Attached & emailed ${email}`);
      setAttaching((a) => { const n = { ...a }; delete n[c.id]; return n; });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setPending((p) => { const n = { ...p }; delete n[c.id]; return n; });
    }
  };

  const shareSMS = (c) => {
    const body = encodeURIComponent(smsText(c.code, c.days, c.plan));
    // iOS uses `sms:&body=`, Android accepts `sms:?body=` — this format works on both.
    window.location.href = `sms:?&body=${body}`;
  };

  if (!user) return null;
  if (!user.is_platform_admin) {
    return (
      <div className="p-12 max-w-xl">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <ShieldAlert className="w-8 h-8 text-slate-400 mx-auto" />
          <div className="font-display font-bold text-xl mt-3">Platform admin only</div>
        </div>
      </div>
    );
  }

  const available = codes.filter((c) => !c.redeemed_by && !c.recipient_email).length;
  const sent = codes.filter((c) => c.recipient_email && !c.redeemed_by).length;
  const redeemed = codes.filter((c) => !!c.redeemed_by).length;

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-5xl space-y-8">
      <PageHeader
        eyebrow="Platform admin"
        title="Beta management"
        description="Mint invite codes, email/SMS them to clinics, and track every redemption."
      />

      {/* Stats */}
      <section className="grid sm:grid-cols-3 gap-3" data-testid="beta-stats">
        <StatCard label="Available" value={available} icon={<Ticket className="w-4 h-4" />} tone="slate" testId="stat-available" />
        <StatCard label="Sent — pending" value={sent} icon={<Clock className="w-4 h-4" />} tone="amber" testId="stat-sent" />
        <StatCard label="Redeemed" value={redeemed} icon={<CheckCircle2 className="w-4 h-4" />} tone="emerald" testId="stat-redeemed" />
      </section>

      {/* Generate */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="beta-generator">
        <div className="flex items-center gap-2 mb-4">
          <Ticket className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">Generate invite codes</div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Days free</Label>
            <Input
              type="number" min={1} max={365}
              value={form.days}
              onChange={(e) => setForm({ ...form, days: Math.max(1, Math.min(365, parseInt(e.target.value || "60", 10))) })}
              className="mt-1.5 rounded-xl" data-testid="beta-days"
            />
          </div>
          <div>
            <Label>Plan granted</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="beta-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="clinic">Clinic (recommended)</SelectItem>
                <SelectItem value="solo">Solo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note (internal)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Wave 1 · March BCBAs"
              className="mt-1.5 rounded-xl" data-testid="beta-note" maxLength={80}
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label>Cohort</Label>
            <Select value={form.access_type} onValueChange={(v) => {
              // Auto-nudge days to match the cohort so admins don't send a
              // "1-year founding" invite that actually expires in 60 days.
              const patch = { access_type: v };
              if (v === "founding_solo_1_year" || v === "founding_clinic_1_year") patch.days = 365;
              if (v === "standard_60_day_beta") patch.days = 60;
              if (v === "founding_solo_1_year") patch.plan = "solo";
              if (v === "founding_clinic_1_year") patch.plan = "clinic";
              setForm({ ...form, ...patch });
            }}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="beta-access-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (recommended — infers from days + plan)</SelectItem>
                <SelectItem value="standard_60_day_beta">Standard beta · 60 days</SelectItem>
                <SelectItem value="founding_solo_1_year">Founding Solo · 1 year (5 seats total)</SelectItem>
                <SelectItem value="founding_clinic_1_year">Founding Clinic · 1 year (5 seats total)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1.5">
              Founding = 1-year full access + special email copy. Capped at 5 seats each.
            </div>
          </div>
        </div>

        <div className="mt-4">
          <Label>Recipient emails <span className="text-slate-400 font-normal">— optional, comma or newline separated</span></Label>
          <Textarea
            value={form.recipients}
            onChange={(e) => setForm({ ...form, recipients: e.target.value })}
            placeholder="one@clinic.com, two@clinic.com&#10;three@clinic.com"
            className="mt-1.5 rounded-xl min-h-[90px]"
            data-testid="beta-recipients"
          />
          <div className="text-xs text-slate-500 mt-1.5">
            {parsedRecipients.length > 0
              ? <>Will mint <strong>{parsedRecipients.length}</strong> code{parsedRecipients.length === 1 ? "" : "s"} and email each recipient.</>
              : <>Leave blank to mint unassigned codes you can copy or attach later.</>
            }
          </div>
        </div>

        {parsedRecipients.length === 0 && (
          <div className="mt-4 max-w-[200px]">
            <Label>Count</Label>
            <Input
              type="number" min={1} max={100}
              value={form.count}
              onChange={(e) => setForm({ ...form, count: Math.max(1, Math.min(100, parseInt(e.target.value || "1", 10))) })}
              className="mt-1.5 rounded-xl" data-testid="beta-count"
            />
          </div>
        )}

        <Button onClick={create} disabled={busy} className="rounded-full mt-5" data-testid="beta-generate-button">
          {busy
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
            : parsedRecipients.length > 0
              ? <><Send className="w-4 h-4 mr-2" /> Mint & email {parsedRecipients.length} invite{parsedRecipients.length === 1 ? "" : "s"}</>
              : <>Generate {effectiveCount} code{effectiveCount === 1 ? "" : "s"}</>
          }
        </Button>
      </section>

      {/* Codes table */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="beta-codes-list">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display font-bold text-lg">All invite codes</div>
            <div className="text-xs text-slate-500">{codes.length} total · newest first</div>
          </div>
        </div>
        {codes.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">No codes yet — generate some above.</div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-sm" data-testid="beta-codes-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-6 py-2 text-left font-semibold">Code</th>
                  <th className="px-2 py-2 text-left font-semibold">Recipient</th>
                  <th className="px-2 py-2 text-left font-semibold">Status</th>
                  <th className="px-6 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {codes.map((c) => {
                  const isRedeemed = !!c.redeemed_by;
                  const isSent = !!c.recipient_email && !isRedeemed;
                  const isAvailable = !c.recipient_email && !isRedeemed;
                  const rowPending = pending[c.id];
                  const showAttach = attaching[c.id] !== undefined;
                  return (
                    <tr key={c.id} data-testid={`beta-code-row-${c.code}`}>
                      <td className="px-6 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <div className={`font-mono font-bold tracking-widest ${isRedeemed ? "text-slate-400 line-through" : "text-slate-900"}`}>{c.code}</div>
                          <button
                            onClick={() => copyCode(c.code)}
                            className="p-1 rounded hover:bg-slate-100 text-slate-500"
                            title="Copy code"
                            data-testid={`beta-copy-${c.code}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {c.plan} · {c.days}d · {formatDate(c.created_at)}
                          {c.note ? <> · {c.note}</> : null}
                        </div>
                      </td>
                      <td className="px-2 py-3 align-top">
                        {c.recipient_email ? (
                          <div>
                            <div className="text-slate-800">{c.recipient_email}</div>
                            {c.email_sent_at && (
                              <div className="text-[11px] text-slate-500">
                                emailed {formatDate(c.email_sent_at)}
                                {c.email_send_count > 1 ? ` · ×${c.email_send_count}` : ""}
                              </div>
                            )}
                            {isRedeemed && c.redeemed_by_email && (
                              <div
                                className={`mt-1.5 text-[11px] flex items-center gap-1 ${
                                  c.redeemed_by_email.toLowerCase() === c.recipient_email.toLowerCase()
                                    ? "text-emerald-700"
                                    : "text-amber-700 font-semibold"
                                }`}
                                data-testid={`redeem-trail-${c.code}`}
                              >
                                <span>→</span>
                                <span>
                                  redeemed by <span className="font-semibold">{c.redeemed_by_email}</span>
                                  {c.redeemed_by_email.toLowerCase() !== c.recipient_email.toLowerCase() && (
                                    <span
                                      className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider"
                                      title="Recipient and redeemer emails differ"
                                    >
                                      Mismatch
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : isRedeemed && c.redeemed_by_email ? (
                          <div className="text-[11px]">
                            <div className="text-slate-400 italic">no recipient attached</div>
                            <div className="mt-1 text-slate-700">
                              → redeemed by <span className="font-semibold">{c.redeemed_by_email}</span>
                            </div>
                          </div>
                        ) : showAttach ? (
                          <div className="flex gap-1 items-center">
                            <Input
                              type="email"
                              placeholder="colleague@clinic.com"
                              value={attaching[c.id] || ""}
                              onChange={(e) => setAttaching((a) => ({ ...a, [c.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === "Enter") attachRecipient(c); }}
                              className="rounded-lg h-8 text-xs"
                              data-testid={`attach-email-input-${c.code}`}
                              autoFocus
                            />
                            <Button
                              size="sm"
                              onClick={() => attachRecipient(c)}
                              disabled={rowPending === "attach"}
                              className="rounded-full h-8"
                              data-testid={`attach-email-save-${c.code}`}
                            >
                              {rowPending === "attach" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send"}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">— unassigned</span>
                        )}
                      </td>
                      <td className="px-2 py-3 align-top">
                        <StatusPill
                          state={isRedeemed ? "redeemed" : isSent ? "sent" : "available"}
                          redeemedBy={c.redeemed_by_email}
                          redeemedAt={c.redeemed_at}
                        />
                      </td>
                      <td className="px-6 py-3 align-top">
                        {isRedeemed ? (
                          <span className="text-[11px] text-slate-400">no actions</span>
                        ) : (
                          <div className="flex justify-end gap-1 flex-wrap">
                            <ActionButton
                              icon={<LinkIcon className="w-3.5 h-3.5" />}
                              label="Link"
                              onClick={() => copyLink(c.code)}
                              testId={`copy-link-${c.code}`}
                            />
                            <ActionButton
                              icon={<MessageSquare className="w-3.5 h-3.5" />}
                              label="SMS"
                              onClick={() => shareSMS(c)}
                              testId={`sms-${c.code}`}
                            />
                            {isAvailable && !showAttach && (
                              <ActionButton
                                icon={<UserPlus className="w-3.5 h-3.5" />}
                                label="Attach email"
                                onClick={() => setAttaching((a) => ({ ...a, [c.id]: "" }))}
                                testId={`attach-${c.code}`}
                              />
                            )}
                            {isSent && (
                              <ActionButton
                                icon={rowPending === "email" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                label={c.email_send_count > 0 ? "Resend" : "Email"}
                                onClick={() => sendEmailFor(c)}
                                disabled={rowPending === "email"}
                                testId={`resend-${c.code}`}
                              />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Object-storage migration */}
      <section
        className="rounded-2xl border border-slate-200 bg-white p-6"
        data-testid="admin-image-migration"
      >
        <div className="flex items-center gap-2 mb-4">
          <HardDrive className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">Object-storage migration</div>
          <div className="ml-auto text-[11px] text-slate-500">
            Legacy base64 → S3/R2 · idempotent · Phase 1 plumbing
          </div>
        </div>
        {migrateStatus ? (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            <StatCard
              label="Pending"
              value={migrateStatus.pending}
              icon={<Clock className="w-3 h-3" />}
              tone={migrateStatus.pending > 0 ? "amber" : "emerald"}
              testId="migrate-pending"
            />
            <StatCard
              label="In storage"
              value={migrateStatus.in_storage}
              icon={<HardDrive className="w-3 h-3" />}
              tone="emerald"
              testId="migrate-in-storage"
            />
            <StatCard
              label="Total w/ image"
              value={migrateStatus.total_with_image}
              icon={<CheckCircle2 className="w-3 h-3" />}
              tone="slate"
              testId="migrate-total"
            />
            <StatCard
              label="Shrinkable"
              value={migrateStatus.shrinkable ?? 0}
              icon={<HardDrive className="w-3 h-3" />}
              tone={(migrateStatus.shrinkable ?? 0) > 0 ? "amber" : "emerald"}
              testId="migrate-shrinkable"
            />
            <StatCard
              label="Storage ready"
              value={migrateStatus.storage_ready ? "Yes" : "No"}
              icon={<ShieldAlert className="w-3 h-3" />}
              tone={migrateStatus.storage_ready ? "emerald" : "amber"}
              testId="migrate-storage-ready"
            />
          </div>
        ) : (
          <div className="text-sm text-slate-500 py-2">Loading status…</div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={runMigration}
            disabled={migrating || !migrateStatus?.storage_ready || (migrateStatus?.pending || 0) === 0}
            data-testid="migrate-images-btn"
          >
            {migrating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Migrating…</>
            ) : (
              <><HardDrive className="w-4 h-4 mr-2" /> Migrate legacy images (batch of 100)</>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={runShrink}
            disabled={shrinking || !migrateStatus?.storage_ready || (migrateStatus?.shrinkable || 0) === 0}
            data-testid="shrink-images-btn"
          >
            {shrinking ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Shrinking…</>
            ) : (
              <><HardDrive className="w-4 h-4 mr-2" /> Shrink DB (drop base64 on migrated)</>
            )}
          </Button>
          {!migrateStatus?.storage_ready && (
            <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              Storage key not initialised — check backend logs.
            </span>
          )}
          {migrateStatus?.storage_ready && (migrateStatus?.pending || 0) === 0 && (migrateStatus?.shrinkable || 0) === 0 && (
            <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              All caught up — nothing to migrate or shrink.
            </span>
          )}
          <div className="text-[11px] text-slate-500">
            Migrate runs 100 at a time · Shrink runs up to 2000 at a time.
          </div>
        </div>
      </section>

      {/* Beta feedback dashboard (Wave 3) */}
      <AdminBetaFeedback />

      {/* System health */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-system-health">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Activity className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">System health</div>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
            {health?.sample_count > 0 && (
              <span>Last {health.sample_count} API calls</span>
            )}
            <button
              type="button"
              onClick={refreshHealth}
              disabled={healthRefreshing}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 hover:border-slate-400 hover:bg-slate-50 text-[11px] font-semibold text-slate-700 disabled:opacity-40"
              data-testid="system-health-refresh"
            >
              {healthRefreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gauge className="w-3 h-3" />}
              Refresh
            </button>
          </div>
        </div>
        {!health || health.sample_count === 0 ? (
          <div className="text-sm text-slate-500 py-2">
            No samples yet — issue a few API calls, then hit Refresh.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <StatCard label="Total samples" value={health.sample_count}
                icon={<Activity className="w-3 h-3" />} tone="slate" testId="health-samples" />
              <StatCard label="Raw payload" value={formatBytes(health.gzip_summary?.raw_bytes)}
                icon={<HardDrive className="w-3 h-3" />} tone="slate" testId="health-raw" />
              <StatCard label="Wire (est.)" value={formatBytes(health.gzip_summary?.wire_bytes_est)}
                icon={<HardDrive className="w-3 h-3" />} tone="emerald" testId="health-wire" />
              <StatCard label="Gzip savings" value={`${health.gzip_summary?.savings_pct ?? 0}%`}
                icon={<Gauge className="w-3 h-3" />} tone="emerald" testId="health-savings" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <HealthTable
                title="Top endpoints by payload"
                rows={health.top_by_size}
                metric="avg_bytes"
                fmt={formatBytes}
                testId="health-top-size"
              />
              <HealthTable
                title="Top endpoints by latency"
                rows={health.top_by_latency}
                metric="avg_ms"
                fmt={(v) => `${v} ms`}
                testId="health-top-latency"
              />
            </div>
            {health.recent_errors?.length > 0 && (
              <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50/50 p-3">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-rose-700 mb-2">
                  <AlertTriangle className="w-3 h-3" /> Recent errors
                </div>
                <ul className="space-y-1 text-xs text-rose-800" data-testid="health-recent-errors">
                  {health.recent_errors.map((e, i) => (
                    <li key={`${e.path}-${i}`} className="flex items-center gap-2">
                      <span className="font-mono bg-rose-100 px-1.5 py-0.5 rounded text-[10px]">{e.status}</span>
                      <span className="font-mono text-[11px]">{e.method}</span>
                      <span className="truncate">{e.path}</span>
                      <span className="ml-auto text-[10px] text-rose-500">{e.ms} ms</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      {/* Feedback */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-feedback-list">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquareQuote className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">Recent feedback</div>
          <div className="ml-auto text-[11px] text-slate-500">Weekly digest → Mondays 08:00 UTC</div>
        </div>
        {feedback.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">No feedback yet.</div>
        ) : (
          <ul className="space-y-4">
            {feedback.map((f) => (
              <li key={f.id} className="border border-slate-100 rounded-xl p-4" data-testid={`feedback-item-${f.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                    f.kind === "bug" ? "bg-rose-100 text-rose-700" :
                    f.kind === "praise" ? "bg-emerald-100 text-emerald-700" :
                    f.kind === "idea" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>{f.kind}</span>
                  {f.team_label && (
                    <span
                      className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 tracking-wider"
                      title={
                        f.team_clinic_name
                          ? `Clinic: ${f.team_clinic_name}`
                          : `Team owner: ${f.team_owner_name || f.team_owner_email}`
                      }
                      data-testid={`feedback-team-${f.id}`}
                    >
                      {f.team_label}
                    </span>
                  )}
                  <span className="text-xs text-slate-600 font-semibold">{f.user_name}</span>
                  <span className="text-xs text-slate-400">&lt;{f.user_email}&gt;</span>
                  <span className="text-[10px] text-slate-400">· {f.plan} · {f.page || "—"} · {formatDate(f.created_at)}</span>
                </div>
                <div className="text-sm text-slate-800 mt-2 whitespace-pre-wrap leading-relaxed">{f.message}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, icon, tone, testId }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase font-bold tracking-wider">
        {icon} {label}
      </div>
      <div className="font-display font-bold text-3xl mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ state, redeemedBy, redeemedAt }) {
  if (state === "redeemed") {
    return (
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
          Redeemed
        </span>
        {redeemedAt && (
          <div className="text-[11px] text-slate-400 mt-1">
            {formatDate(redeemedAt)}
          </div>
        )}
      </div>
    );
  }
  if (state === "sent") {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
        Sent · pending
      </span>
    );
  }
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
      Available
    </span>
  );
}

function ActionButton({ icon, label, onClick, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 hover:border-slate-400 hover:bg-slate-50 text-[11px] font-semibold text-slate-700 disabled:opacity-40"
      data-testid={testId}
    >
      {icon} {label}
    </button>
  );
}

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function HealthTable({ title, rows, metric, fmt, testId }) {
  return (
    <div className="rounded-xl border border-slate-100 overflow-hidden" data-testid={testId}>
      <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100">
        {title}
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="p-3 text-xs text-slate-400">No samples.</div>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.method}-${r.path}`} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500 w-14">{r.method}</td>
                <td className="px-1 py-1.5 font-mono text-[11px] text-slate-800 truncate max-w-0">{r.path}</td>
                <td className="px-3 py-1.5 text-right text-slate-400 text-[10px] w-14">×{r.count}</td>
                <td className="px-3 py-1.5 text-right font-semibold text-slate-800 w-24">{fmt(r[metric])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
